import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import {
  trackSearchPerformed,
  trackSearchNoResults,
  trackCategoryViewed,
} from "@/lib/analytics";
import { useListCategories, useListListings, useGetStats } from "@workspace/api-client-react";
import { useI18n } from "@/contexts/I18nContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Search,
  MapPin,
  Store,
  Filter,
  LayoutGrid,
  List as ListIcon,
  Sparkles,
  ArrowRight,
  CarFront,
  Building2,
  BriefcaseBusiness,
  PackageSearch,
} from "lucide-react";
import { ListingImage } from "@/components/listing-image";
import { Link } from "wouter";

export default function ExplorePage() {
  const { t, language } = useI18n();
  const [, setLocation] = useLocation();
  const searchParams = new URLSearchParams(window.location.search);

  const [q, setQ] = useState(searchParams.get("q") || "");
  const [type, setType] = useState<"product" | "service" | undefined>(
    (searchParams.get("type") as "product" | "service") || undefined
  );
  const [categoryId, setCategoryId] = useState<string>(searchParams.get("categoryId") || "all");
  const [area, setArea] = useState<string>(searchParams.get("area") || "all");
  const [featured, setFeatured] = useState<boolean>(searchParams.get("featured") === "true");
  const [serviceMethod, setServiceMethod] = useState<string>(searchParams.get("serviceMethod") || "all");
  const [sort, setSort] = useState<string>(searchParams.get("sort") || "newest");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [demandOpen, setDemandOpen] = useState(false);
  const [demandStatus, setDemandStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [demandReference, setDemandReference] = useState("");

  const { data: categories } = useListCategories();
  const { data: stats } = useGetStats();

  // ── Analytics refs (prevent duplicate events on re-renders) ──────────────
  const lastSearchKey = useRef("");
  const lastCategoryId = useRef("");

  const { data: results, isLoading, isError } = useListListings({
    q: q || undefined,
    type,
    categoryId: categoryId !== "all" ? Number(categoryId) : undefined,
    area: area !== "all" ? area : undefined,
    featured: featured ? true : undefined,
    serviceMethod: serviceMethod !== "all" ? serviceMethod : undefined,
    sort: sort as "newest" | "featured" | "name",
  });

  // Track search events after results settle
  useEffect(() => {
    if (isLoading || !results) return;
    const catName = categoryId !== "all"
      ? (categories?.find((c) => c.id.toString() === categoryId)?.name)
      : undefined;
    const key = `${q}|${categoryId}|${area}|${results.total}`;
    if (key === lastSearchKey.current) return;
    lastSearchKey.current = key;
    if (!q && categoryId === "all") return; // no active search filter
    if ((results.listings?.length ?? 0) === 0) {
      trackSearchNoResults({ search_term: q || undefined, category: catName });
    } else {
      trackSearchPerformed({ search_term: q || undefined, category: catName });
    }
  }, [results, isLoading, q, categoryId, area, categories]);

  // Track category_viewed when the category filter is explicitly set
  useEffect(() => {
    if (categoryId === "all" || categoryId === lastCategoryId.current) return;
    lastCategoryId.current = categoryId;
    const cat = categories?.find((c) => c.id.toString() === categoryId);
    if (cat) trackCategoryViewed({ category: cat.name });
  }, [categoryId, categories]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (type) params.set("type", type);
    if (categoryId !== "all") params.set("categoryId", categoryId);
    if (area !== "all") params.set("area", area);
    if (featured) params.set("featured", "true");
    if (serviceMethod !== "all") params.set("serviceMethod", serviceMethod);
    if (sort !== "newest") params.set("sort", sort);

    const newSearch = params.toString();
    const newPath = `/explore${newSearch ? `?${newSearch}` : ""}`;

    if (`/explore${window.location.search}` !== newPath) {
      setLocation(newPath, { replace: true });
    }
  }, [q, type, categoryId, area, featured, serviceMethod, sort, setLocation]);

  const clearFilters = () => {
    setQ("");
    setType(undefined);
    setCategoryId("all");
    setArea("all");
    setFeatured(false);
    setServiceMethod("all");
    setSort("newest");
  };

  const submitDemand = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDemandStatus("sending");
    const form = new FormData(event.currentTarget);
    try {
      const response = await fetch("/api/demand-requests", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requestText: form.get("requestText"), categoryId: categoryId === "all" ? undefined : Number(categoryId),
          area: form.get("area") || (area === "all" ? "Dubai" : area), budgetAed: form.get("budgetAed") ? Number(form.get("budgetAed")) : undefined,
          requiredDate: form.get("requiredDate") || undefined, customerName: form.get("customerName"),
          customerWhatsapp: form.get("customerWhatsapp"), customerEmail: form.get("customerEmail") || undefined, consentGiven: form.get("consentGiven") === "on",
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setDemandReference(result.referenceCode);
      setDemandStatus("success");
    } catch {
      setDemandStatus("error");
    }
  };

  return (
    <div className="flex-1 w-full bg-muted/20 flex flex-col md:flex-row">
      {/* Sidebar Filters */}
      <aside className="w-full md:w-72 bg-card border-r border-border/50 p-6 flex flex-col gap-8 shrink-0 overflow-y-auto">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5" /> {t("Filters")}
          </h2>

          {/* Keyword */}
          <div className="space-y-2 mb-6">
            <Label htmlFor="search">{t("Search")}</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                placeholder={t("Keywords...")}
                className="pl-9"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="input-search-filter"
              />
            </div>
          </div>

          {/* Type Toggle */}
          <div className="space-y-3 mb-6">
            <Label>{t("Type")}</Label>
            <div className="flex bg-muted p-1 rounded-lg">
              <button
                className={`flex-1 text-sm py-2 rounded-md font-medium transition-all ${!type ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                onClick={() => setType(undefined)}
                data-testid="btn-type-all"
              >
                {t("All")}
              </button>
              <button
                className={`flex-1 text-sm py-2 rounded-md font-medium transition-all ${type === "product" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                onClick={() => setType("product")}
                data-testid="btn-type-products"
              >
                {t("Products")}
              </button>
              <button
                className={`flex-1 text-sm py-2 rounded-md font-medium transition-all ${type === "service" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                onClick={() => setType("service")}
                data-testid="btn-type-services"
              >
                {t("Services")}
              </button>
            </div>
          </div>

          {/* Category */}
          <div className="space-y-2 mb-6">
            <Label>{t("Category")}</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger data-testid="select-category-filter">
                <SelectValue placeholder={t("All Categories")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("All Categories")}</SelectItem>
                {categories?.map((c) => (
                  <SelectItem key={c.id} value={c.id.toString()}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Area */}
          <div className="space-y-2 mb-6">
            <Label>{t("Dubai Area")}</Label>
            <Select value={area} onValueChange={setArea}>
              <SelectTrigger data-testid="select-area-filter">
                <SelectValue placeholder={t("All Areas")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("All Areas")}</SelectItem>
                {stats?.areas?.map((a) => (
                  <SelectItem key={a} value={a}>
                    {a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Featured Only */}
          <div className="space-y-2 mb-6">
            <Label>{t("How do you serve customers?")}</Label>
            <Select value={serviceMethod} onValueChange={setServiceMethod}>
              <SelectTrigger data-testid="select-service-method-filter">
                <SelectValue placeholder={t("All service methods")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("All service methods")}</SelectItem>
                <SelectItem value="merchant_location">{t("Service at Merchant Location")}</SelectItem>
                <SelectItem value="customer_location">{t("Service at Customer Location / Home Service")}</SelectItem>
                <SelectItem value="physical_goods">{t("Physical Goods")}</SelectItem>
                <SelectItem value="delivery">{t("Delivery Available")}</SelectItem>
                <SelectItem value="pickup">{t("Pickup Available")}</SelectItem>
                <SelectItem value="online_remote">{t("Online / Remote Service")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Featured Only */}
          <div className="flex items-center justify-between mb-8">
            <Label htmlFor="featured-toggle" className="cursor-pointer">
              {t("Premium Only")}
            </Label>
            <Switch
              id="featured-toggle"
              checked={featured}
              onCheckedChange={setFeatured}
              data-testid="switch-premium-only"
            />
          </div>

          <Button variant="outline" className="w-full" onClick={clearFilters} data-testid="btn-clear-filters">
            {t("Clear Filters")}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 lg:p-10 flex flex-col h-full overflow-hidden">
        {/* Top bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t("Explore Dubai")}</h1>
            <p className="text-muted-foreground text-sm">
              {t("Showing")} {results?.total || 0} {t("results")}
            </p>
          </div>

          <div className="flex items-center gap-3 self-end sm:self-auto">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[140px] h-9" data-testid="select-sort">
                <SelectValue placeholder={t("Sort by")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t("Newest")}</SelectItem>
                <SelectItem value="featured">{t("Featured")}</SelectItem>
                <SelectItem value="name">{t("Name (A-Z)")}</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center bg-card border border-border rounded-md">
              <Button
                variant="ghost"
                size="icon"
                className={`h-9 w-9 rounded-none rounded-l-md ${view === "grid" ? "bg-muted" : ""}`}
                onClick={() => setView("grid")}
                data-testid="btn-view-grid"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={`h-9 w-9 rounded-none rounded-r-md ${view === "list" ? "bg-muted" : ""}`}
                onClick={() => setView("list")}
                data-testid="btn-view-list"
              >
                <ListIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto pr-2 pb-10">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center min-h-64 text-center border-2 border-dashed border-destructive/30 rounded-xl p-6" data-testid="listings-error-state">
              <Search className="h-10 w-10 text-muted-foreground mb-4 opacity-50" />
              <h3 className="text-xl font-bold mb-2">{t("Something went wrong")}</h3>
              <p className="text-muted-foreground mb-6 max-w-md">
                {t("We couldn't load listings right now. Please try again.")}
              </p>
              <Button onClick={() => window.location.reload()} data-testid="btn-retry-listings">
                {t("Try Again")}
              </Button>
            </div>
          ) : results?.listings?.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-64 text-center border-2 border-dashed border-border rounded-xl p-6">
              <Search className="h-10 w-10 text-muted-foreground mb-4 opacity-50" />
              <h3 className="text-xl font-bold mb-2">{t("No results found")}</h3>
              <p className="text-muted-foreground mb-6 max-w-md">
                {t("Try adjusting your filters to find what you're looking for.")}
              </p>
              <Button onClick={clearFilters} data-testid="btn-clear-filters-empty">
                {t("Clear Filters")}
              </Button>
              <section
                className="relative mt-8 w-full max-w-3xl overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-[#071d3b] via-[#0b3a4d] to-[#08745c] p-6 text-start text-white shadow-xl shadow-primary/15 sm:p-8"
                aria-labelledby="souq-concierge-heading"
                data-testid="souq-concierge-cta"
              >
                <div className="pointer-events-none absolute -top-24 -right-20 h-52 w-52 rounded-full bg-primary/25 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-28 -left-20 h-52 w-52 rounded-full bg-amber-300/10 blur-3xl" />
                <div className="relative flex flex-col gap-6 sm:flex-row sm:items-start">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-primary-foreground/20 bg-primary text-primary-foreground shadow-lg shadow-emerald-950/30">
                    <Sparkles className="h-7 w-7" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="mb-2 text-xs font-bold tracking-[0.18em] text-amber-200">
                      {language === "ar" ? "مساعد سوق" : "SOUQ CONCIERGE"}
                    </p>
                    <h3 id="souq-concierge-heading" className="max-w-xl text-2xl font-extrabold tracking-tight sm:text-3xl">
                      {language === "ar" ? "ما لقيت اللي بدك ياه؟ اطلب أي شي." : "Can’t find it? Ask us for anything."}
                    </h3>
                    <p className="mt-3 max-w-2xl text-sm leading-6 text-white/80 sm:text-base">
                      {language === "ar"
                        ? "خبرنا شو عم تدور عليه—من سيارة أو عقار لخدمة أو منتج صعب تلاقيه—ومنساعدك تتواصل مع تاجر يقبل USDT."
                        : "Tell us what you’re looking for—from a car or property to a service or hard-to-find product—and we’ll help connect you with a merchant that accepts USDT."}
                    </p>
                    <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2" aria-label={language === "ar" ? "أمثلة على الطلبات" : "Request examples"}>
                      {(language === "ar"
                        ? [
                            { label: "سيارة محددة", Icon: CarFront, accent: "text-primary" },
                            { label: "عقار في دبي", Icon: Building2, accent: "text-amber-700" },
                            { label: "خدمة للأعمال", Icon: BriefcaseBusiness, accent: "text-primary" },
                            { label: "شيء آخر", Icon: PackageSearch, accent: "text-amber-700" },
                          ]
                        : [
                            { label: "A specific car", Icon: CarFront, accent: "text-primary" },
                            { label: "Property in Dubai", Icon: Building2, accent: "text-amber-700" },
                            { label: "Business service", Icon: BriefcaseBusiness, accent: "text-primary" },
                            { label: "Something else", Icon: PackageSearch, accent: "text-amber-700" },
                          ]
                      ).map(({ label, Icon, accent }) => (
                        <span
                          key={label}
                          className="group flex min-h-14 items-center gap-3 rounded-xl border border-emerald-700/30 bg-white/95 px-4 py-3 text-sm font-bold text-[#071d3b] shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-amber-500/70 hover:bg-amber-50 hover:shadow-md"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-emerald-100 bg-emerald-50 transition-colors duration-200 group-hover:border-amber-200 group-hover:bg-amber-100">
                            <Icon className={`h-5 w-5 ${accent}`} aria-hidden="true" />
                          </span>
                          <span className="min-w-0 leading-5">{label}</span>
                        </span>
                      ))}
                    </div>
                    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                      <Button
                        type="button"
                        onClick={() => setDemandOpen(true)}
                        className="group h-12 w-full bg-primary px-5 text-base font-bold text-primary-foreground shadow-lg shadow-emerald-950/30 transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b3a4d] sm:w-auto"
                        data-testid="btn-open-demand-request"
                      >
                        {language === "ar" ? "خبرنا شو بدك" : "Tell us what you need"}
                        <ArrowRight className={`h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 ${language === "ar" ? "mr-2 rotate-180 group-hover:-translate-x-1" : "ml-2"}`} aria-hidden="true" />
                      </Button>
                      <p className="text-center text-xs font-medium text-white/70 sm:text-start">
                        {language === "ar" ? "الطلب مجاني • ما في دفع لـ Souq Crypto" : "Free to request • No payment to Souq Crypto"}
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              {demandOpen && (
                <form onSubmit={submitDemand} className="mt-5 w-full max-w-lg space-y-3 rounded-xl border bg-card p-5 text-start">
                  {demandStatus === "success" ? <div className="text-center"><p className="font-semibold">{language === "ar" ? "تم استلام الطلب" : "Request received"}</p><p className="mt-1 text-sm text-muted-foreground">{language === "ar" ? "مرجعك الخاص:" : "Your private reference:"} <b className="font-mono text-foreground" dir="ltr">{demandReference}</b></p></div> : <>
                    <p className="text-sm text-muted-foreground">{language === "ar" ? "أخبرنا بما تحتاجه في دبي. نستخدم بياناتك فقط للمساعدة في ربطك بتاجر مناسب." : "Tell us what you need in Dubai. We will only use your details to help connect you with a relevant merchant."}</p>
                    <Textarea name="requestText" required minLength={3} placeholder={language === "ar" ? "المنتج أو الخدمة التي تحتاجها" : "Product or service you need"} />
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2"><Input name="area" required placeholder={language === "ar" ? "منطقة دبي" : "Dubai area"} defaultValue={area === "all" ? "" : area} /><Input name="budgetAed" type="number" min="0" placeholder={language === "ar" ? "الميزانية بـ AED (اختياري)" : "Budget in AED (optional)"} dir="ltr" /></div>
                    <Input name="requiredDate" type="date" />
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2"><Input name="customerName" required placeholder={language === "ar" ? "اسمك" : "Your name"} /><Input name="customerWhatsapp" required placeholder={language === "ar" ? "رقم WhatsApp" : "WhatsApp number"} dir="ltr" /></div>
                    <Input name="customerEmail" type="email" placeholder={language === "ar" ? "البريد الإلكتروني (اختياري)" : "Email (optional)"} dir="ltr" />
                    <label className="flex gap-2 text-xs text-muted-foreground"><input name="consentGiven" type="checkbox" required /> {language === "ar" ? "أوافق على أن يستخدم Souq Crypto هذه البيانات للمساعدة في ربطي بتاجر مستقل. لا يتعامل Souq Crypto بالأموال." : "I agree that Souq Crypto may use these details to help connect me with an independent merchant. Souq Crypto does not handle funds."}</label>
                    {demandStatus === "error" ? <p className="text-sm text-destructive">{language === "ar" ? "تعذر إرسال طلبك. يرجى المحاولة مرة أخرى." : "We could not submit your request. Please try again."}</p> : null}
                    <div className="flex gap-2"><Button type="submit" disabled={demandStatus === "sending"}>{demandStatus === "sending" ? (language === "ar" ? "جارٍ الإرسال…" : "Sending…") : (language === "ar" ? "إرسال الطلب" : "Send request")}</Button><Button type="button" variant="ghost" onClick={() => setDemandOpen(false)}>{language === "ar" ? "إلغاء" : "Cancel"}</Button></div>
                  </>}
                </form>
              )}
            </div>
          ) : (
            <div
              className={
                view === "grid"
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                  : "flex flex-col gap-4"
              }
            >
              {results?.listings?.map((listing) => (
                <Link
                  key={listing.id}
                  href={`/listings/${listing.slug ?? listing.id}`}
                  className="block h-full group"
                  data-testid={`listing-${listing.id}`}
                >
                  <Card
                    className={`overflow-hidden rounded-2xl border-border/60 hover:border-primary/50 hover:shadow-xl transition-all duration-300 ${
                      view === "list" ? "flex flex-row h-48" : "flex flex-col h-full"
                    }`}
                  >
                    <div
                      className={`relative overflow-hidden bg-muted ${
                        view === "list" ? "w-48 shrink-0" : "aspect-[4/3] w-full"
                      }`}
                    >
                      <ListingImage
                        src={listing.imageUrl}
                        alt={listing.title}
                        imgClassName="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
                        <Badge className="souq-usdt-badge bg-primary/95 hover:bg-primary text-primary-foreground backdrop-blur-sm shadow-sm font-semibold">
                          {t("USDT Accepted")}
                        </Badge>
                        {listing.isFeatured && (
                          <Badge
                            variant="outline"
                            className="bg-accent/90 border-accent-foreground/10 text-accent-foreground backdrop-blur-sm"
                          >
                            {t("Premium Only")}
                          </Badge>
                        )}
                      </div>
                      <div className="absolute bottom-3 right-3">
                        <Badge
                          variant="secondary"
                          className="souq-category-badge backdrop-blur-md font-medium shadow-sm"
                        >
                          {listing.categoryName}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex-1 flex flex-col p-5">
                      <div className="mb-2">
                        <h3 className="font-bold text-lg line-clamp-2 group-hover:text-primary transition-colors">
                          {listing.title}
                        </h3>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3 font-medium">
                        <Store className="h-3.5 w-3.5" />
                        <span className="truncate">{listing.merchantName}</span>
                        <span>•</span>
                        <MapPin className="h-3.5 w-3.5" />
                        <span className="truncate">{listing.area}</span>
                      </div>

                      <p
                        className={`text-muted-foreground text-sm flex-1 ${
                          view === "list" ? "line-clamp-3" : "line-clamp-2"
                        }`}
                      >
                        {listing.description}
                      </p>
                      {listing.serviceMethods?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {listing.serviceMethods.map((method) => (
                            <Badge key={method} variant="outline" className="text-[10px]">
                              {t(serviceMethodLabel(method) as Parameters<typeof t>[0])}
                            </Badge>
                          ))}
                        </div>
                      )}

                      <div className="pt-4 mt-auto border-t border-border/50 flex items-center justify-between">
                        <div className="font-bold text-lg">
                          {listing.priceAed
                            ? `${listing.priceAed.toLocaleString()} ${t("AED")}`
                            : t("Ask price")}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="group-hover:bg-primary group-hover:text-primary-foreground"
                          data-testid={`btn-view-details-${listing.id}`}
                        >
                          {t("View Details")}
                        </Button>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function serviceMethodLabel(method: string) {
  return ({
    merchant_location: "Service at Merchant Location",
    customer_location: "Service at Customer Location / Home Service",
    physical_goods: "Physical Goods",
    delivery: "Delivery Available",
    pickup: "Pickup Available",
    online_remote: "Online / Remote Service",
  } as Record<string, string>)[method] ?? method;
}
